import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/auth-context';
import { useUpdateNotificationPreferencesAction } from '@/hooks/actions';
import { useNotificationPreferencesQuery } from '@/hooks/queries/use-notification-preferences-query';
import {
  EmailConfigurableNotificationType,
  NotificationPreferenceEmailDelivery,
  NotificationType,
  type EmailConfigurableNotificationType as EmailConfigurableNotificationTypeValue,
} from '@fluenceur/types';
import {
  Box,
  Button,
  Grid,
  HStack,
  Heading,
  Separator,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

const NOTIFICATION_TYPE_COPY: Record<
  EmailConfigurableNotificationTypeValue,
  { titleKey: string; descriptionKey: string }
> = {
  [NotificationType.FLOW_TEAM_NOTIFIED]: {
    titleKey: 'settings.notificationPreferences.types.flowTeam.title',
    descriptionKey: 'settings.notificationPreferences.types.flowTeam.description',
  },
  [NotificationType.AGENT_RECOMMENDATIONS_DELIVERED]: {
    titleKey: 'settings.notificationPreferences.types.recommendations.title',
    descriptionKey: 'settings.notificationPreferences.types.recommendations.description',
  },
  [NotificationType.AFFILIATE_JOINED_PROGRAM]: {
    titleKey: 'settings.notificationPreferences.types.affiliateJoined.title',
    descriptionKey: 'settings.notificationPreferences.types.affiliateJoined.description',
  },
  [NotificationType.BRAND_SPACE_APPLICATION_RECEIVED]: {
    titleKey: 'settings.notificationPreferences.types.brandSpaceApplication.title',
    descriptionKey: 'settings.notificationPreferences.types.brandSpaceApplication.description',
  },
  [NotificationType.GIFT_CLAIMED]: {
    titleKey: 'settings.notificationPreferences.types.giftClaimed.title',
    descriptionKey: 'settings.notificationPreferences.types.giftClaimed.description',
  },
  [NotificationType.COLLAB_PAYMENT_FAILED]: {
    titleKey: 'settings.notificationPreferences.types.collabPaymentFailed.title',
    descriptionKey: 'settings.notificationPreferences.types.collabPaymentFailed.description',
  },
  [EmailConfigurableNotificationType.BRAND_FIRST_SALE]: {
    titleKey: 'settings.notificationPreferences.types.brandFirstSale.title',
    descriptionKey: 'settings.notificationPreferences.types.brandFirstSale.description',
  },
  [EmailConfigurableNotificationType.BRAND_ONBOARDING_TIPS]: {
    titleKey: 'settings.notificationPreferences.types.onboardingTips.title',
    descriptionKey: 'settings.notificationPreferences.types.onboardingTips.description',
  },
  [NotificationType.GIFT_STOCK_ALERT]: {
    titleKey: 'settings.notificationPreferences.types.giftStockAlert.title',
    descriptionKey: 'settings.notificationPreferences.types.giftStockAlert.description',
  },
  [NotificationType.CONVERSATION_CREATOR_MESSAGE_RECEIVED]: {
    titleKey: 'settings.notificationPreferences.types.creatorMessage.title',
    descriptionKey: 'settings.notificationPreferences.types.creatorMessage.description',
  },
  [NotificationType.BRIEF_APPLICATION_RECEIVED]: {
    titleKey: 'settings.notificationPreferences.types.briefApplication.title',
    descriptionKey: 'settings.notificationPreferences.types.briefApplication.description',
  },
};

const NotificationsSection = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const brandId = user?.currentBrand?.id ?? user?.currentBrandId ?? null;
  const preferencesQuery = useNotificationPreferencesQuery(brandId);
  const updatePreferences = useUpdateNotificationPreferencesAction(brandId);

  if (!brandId) {
    return (
      <Text fontSize="sm" color="fg.muted">
        {t('settings.notificationPreferences.noBrand')}
      </Text>
    );
  }

  if (preferencesQuery.isLoading) {
    return (
      <HStack gap={3} color="fg.muted">
        <Spinner size="sm" />
        <Text fontSize="sm">{t('settings.notificationPreferences.loading')}</Text>
      </HStack>
    );
  }

  if (
    preferencesQuery.isError ||
    !preferencesQuery.data ||
    preferencesQuery.data.brandId !== brandId
  ) {
    return (
      <VStack align="flex-start" gap={3}>
        <Text fontSize="sm" color="fg.muted">
          {t('settings.notificationPreferences.loadError')}
        </Text>
        <Button size="sm" variant="outline" onClick={() => preferencesQuery.refetch()}>
          {t('common.retry')}
        </Button>
      </VStack>
    );
  }

  const handleEmailDeliveryChange = (
    notificationType: EmailConfigurableNotificationTypeValue,
    checked: boolean
  ) => {
    updatePreferences.mutate({
      preferences: [
        {
          notificationType,
          settings: {
            emailDelivery: checked
              ? NotificationPreferenceEmailDelivery.IMMEDIATE
              : NotificationPreferenceEmailDelivery.OFF,
          },
        },
      ],
    });
  };

  return (
    <VStack align="stretch" gap={0}>
      {preferencesQuery.data.preferences.map((preference, index) => {
        const copy = NOTIFICATION_TYPE_COPY[preference.notificationType];
        const checked =
          preference.settings.emailDelivery === NotificationPreferenceEmailDelivery.IMMEDIATE;
        const isLastPreference = index === preferencesQuery.data.preferences.length - 1;

        return (
          <Box key={preference.notificationType}>
            <Grid
              templateColumns={{ base: '1fr', md: '260px 1fr' }}
              gap={{ base: 4, md: 10 }}
              py={{ base: 6, md: 8 }}
              w="full"
            >
              <VStack align="flex-start" gap={2}>
                <Heading as="h2" size="md">
                  {t(copy.titleKey)}
                </Heading>
                <Text fontSize="sm" color="fg.muted">
                  {t(copy.descriptionKey)}
                </Text>
              </VStack>

              <HStack justify={{ base: 'flex-start', md: 'flex-end' }} align="flex-start">
                <Switch
                  checked={checked}
                  disabled={updatePreferences.isPending}
                  colorPalette="primary"
                  flexShrink={0}
                  onCheckedChange={({ checked: nextChecked }) =>
                    handleEmailDeliveryChange(preference.notificationType, Boolean(nextChecked))
                  }
                  inputProps={{
                    'aria-label': t('settings.notificationPreferences.emailSwitchLabel', {
                      notification: t(copy.titleKey),
                    }),
                  }}
                >
                  <Text fontSize="sm" color="fg.muted">
                    {t('settings.notificationPreferences.email')}
                  </Text>
                </Switch>
              </HStack>
            </Grid>
            {!isLastPreference && <Separator />}
          </Box>
        );
      })}
    </VStack>
  );
};

NotificationsSection.displayName = 'NotificationsSection';

export default NotificationsSection;
